import {
  type Dispatch,
  type FunctionComponent,
  type SetStateAction,
  type SVGProps,
  useState,
} from "react";

import { MenuSearchInput } from "common/components/MenuSearchInput";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import FormField from "common/components/ui/FormField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectTrigger,
  SelectValue,
} from "common/components/ui/Select";
import SelectButton from "common/components/ui/SelectButton";
import useCrmSources from "common/datahooks/useCrmSources";
import useIntegrationSettings from "common/datahooks/useIntegrationSettings";
import { useCampaignContext } from "common/helpers/CampaignContext";
import { typedObjectEntries } from "common/helpers/utils";
import type { IntegrationCrmType } from "common/types";

import useCampaignMutations from "../../../../datahooks/useCampaignMutations";

import InstantlyIcon from "assets/icons/instantly.svg?react";
import SmartleadIcon from "assets/icons/smartlead.svg?react";
import CrmBanner from "assets/images/crm-campaign.png";

const crmTypeOptions: Record<
  IntegrationCrmType,
  { label: string; icon: FunctionComponent<SVGProps<SVGSVGElement>> }
> = {
  instantly: { label: "Instantly", icon: InstantlyIcon },
  smartlead: { label: "Smartlead", icon: SmartleadIcon },
};

interface CrmListsProps {
  setSelectSource: Dispatch<SetStateAction<{ id: string; name: string }>>;
}

export default function CrmSources({ setSelectSource }: CrmListsProps) {
  const [inputValue, setInputValue] = useState("");
  const { id: campaignId, crm_type: crmType } = useCampaignContext();
  const { updateCampaign } = useCampaignMutations();

  const { integrationSettings, isLoadingIntegrationSettings } =
    useIntegrationSettings();

  const sourceType = crmType === "instantly" ? "lists" : "campaigns";

  const { crmSources, iseLoadingCrmSources } = useCrmSources({
    crmType,
    sourceType,
  });

  function changeCrmType(newType: IntegrationCrmType) {
    updateCampaign({ campaignId, updates: { crm_type: newType } });
  }

  const options = crmSources || [];
  const lowercasedInput = inputValue.toLowerCase();
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(lowercasedInput),
  );

  let dropdownContent = (
    <p className="p-4 text-center text-black-500">No {sourceType} found</p>
  );

  if (options.length) {
    dropdownContent =
      filteredOptions.length > 0 ? (
        <DropdownMenuList>
          {filteredOptions.map((option) => (
            <DropdownMenuItem
              key={option.id}
              onClick={() => setSelectSource(option)}
            >
              {option.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuList>
      ) : (
        <p className="break-words p-4 text-center text-black-500">
          No results found for&nbsp;
          <span className="text-black-950">{inputValue}</span>
        </p>
      );
  }

  return (
    <section className="mx-auto flex w-full max-w-[717px] flex-col lg:rounded-5xl lg:border lg:border-black-200 lg:p-5 lg:shadow-md">
      <img
        src={CrmBanner}
        width={677}
        height={210}
        alt=""
        className="mb-5 rounded-2xl"
      />

      <div className="flex flex-col gap-2.5 lg:flex-row">
        <FormField label="Select CRM" htmlFor="miner-account-selector">
          <Select value={crmType || undefined} onValueChange={changeCrmType}>
            <SelectTrigger asChild>
              <FilterButton
                className="lg:w-48"
                isLoading={isLoadingIntegrationSettings}
              >
                <SelectValue placeholder="Select a CRM" />
              </FilterButton>
            </SelectTrigger>

            <SelectContent>
              {typedObjectEntries(crmTypeOptions).map(
                ([key, { label, icon: Icon }]) => {
                  const isDisabled = !integrationSettings?.[key];

                  return (
                    <SelectItem key={key} value={key} disabled={isDisabled}>
                      <Icon />
                      <SelectItemText>{label}</SelectItemText>
                    </SelectItem>
                  );
                },
              )}
            </SelectContent>
          </Select>
        </FormField>

        <FormField
          label="Select a source"
          htmlFor="miner-account-selector"
          hint="We wil use this source to find targets for your campaign"
          className="w-full"
        >
          <DropdownMenu>
            <DropdownMenuTrigger disabled={!crmType} asChild>
              <SelectButton
                hasSelection={false}
                isLoading={iseLoadingCrmSources}
              >
                Select a targeting source
              </SelectButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent className="w-[--radix-popper-anchor-width]">
              {options.length > 2 && (
                <MenuSearchInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              )}

              {dropdownContent}
            </DropdownMenuContent>
          </DropdownMenu>
        </FormField>
      </div>
    </section>
  );
}
